import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getCalendarAvailability } from 'lib/API/calendar';
import PropTypes from 'prop-types';
import { grey } from '@mui/material/colors';

const ReseaAppointmentScheduler = ({ open, onComplete, onAppointmentSelected, isRescheduling = false }) => {
  const theme = useTheme();
  const intl = useIntl();

  const requirementSectionRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(isRescheduling ? 'selection' : 'requirement');
  const [acknowledgedRequirement, setAcknowledgedRequirement] = useState(false);

  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [liveRegionMessage, setLiveRegionMessage] = useState('');

  useEffect(() => {
    if (open) {
      loadInitialAvailability();
    }
  }, [open]);

  useEffect(() => {
    if (currentStep === 'requirement' && requirementSectionRef.current) {
      requirementSectionRef.current.focus();
    }
  }, [currentStep]);

  /**
   * Maps backend error codes to i18n message keys.
   *
   * Backend error codes:
   *  1004 — invalid date format
   *  1005 — not a weekday
   *  1006 — date too early (BD1–BD3)
   *  1007 — date before session date
   *  1009 — date outside BD4–BD8 window
   *  1010 — appointment date must be weekday (schedule endpoint)
   *  1011 — invalid start time (schedule endpoint)
   *  1012 — invalid date/time combo (schedule endpoint)
   *  2000 — no slots available (generic; sub-classified by reason_code)
   *
   * reason_code sub-codes (all returned under error code 2000):
   *  "01" — shared calendar all-day event blocks entire day
   *  "02" — all individual members OOF / all-day
   *  "03" — mixed: some members blocked + remaining slots booked
   *  "04" — all members passed day-check but every slot is booked
   *  "00" — no slots across entire BD4–BD8 window (no-selected-date path)
   */
  const getErrorMessage = (error) => {
    const detail = error.response?.data?.detail || error.response?.data || {};
    const errorCode = detail.code;
    const reasonCode = detail.reason_code;
    const backendMessage = detail.message || error.message;

    // reason_code "00" means no slots found across the whole window
    // reason_code "01"/"02" means fully blocked by OOF/shared calendar
    // reason_code "03"/"04" means slots exist but all booked
    // All use error code 2000 — map to appropriate UI message by reason_code
    if (errorCode === 2000) {
      switch (reasonCode) {
        case '01':
        case '02':
          // Shared calendar blocked or all members OOF — treat as "no appointments" (was 2003)
          return intl.formatMessage({ id: 'uiDashboard.resea.errors.2000' });
        case '03':
        case '04':
          // All slots are fully booked — treat as "fully booked" (was 2001)
          return intl.formatMessage({ id: 'uiDashboard.resea.errors.2000' });
        case '00':
        default:
          // No slots available across the entire window
          return intl.formatMessage({ id: 'uiDashboard.resea.errors.2000' });
      }
    }

    const errorKeyMap = {
      1004: 'uiDashboard.resea.errors.1004',
      1005: 'uiDashboard.resea.errors.1005',
      1006: 'uiDashboard.resea.errors.1006',
      1007: 'uiDashboard.resea.errors.1007',
      1009: 'uiDashboard.resea.errors.1009',
      1010: 'uiDashboard.resea.errors.1010',
      1011: 'uiDashboard.resea.errors.1011',
      1012: 'uiDashboard.resea.errors.1012',
    };

    if (errorCode && errorKeyMap[errorCode]) {
      return intl.formatMessage({ id: errorKeyMap[errorCode] });
    }

    return backendMessage || intl.formatMessage({ id: 'uiDashboard.resea.errors.generic' });
  };

  /**
   * Returns true if the error represents a slot conflict (i.e., the slot was
   * taken between the user selecting it and submitting).
   *
   * Backend returns error code 2000 with reason_code "03" or "04" for booked slots.
   * reason_code "01" / "02" are OOF/calendar-blocked (not a conflict per se).
   */
  const isConflictError = (error) => {
    const detail = error.response?.data?.detail || error.response?.data || {};
    const errorCode = detail.code;
    const reasonCode = detail.reason_code;
    return errorCode === 2000 && (reasonCode === '03' || reasonCode === '04');
  };

  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  /**
   * Fetches initial availability (no selected_date).
   *
   * Backend response shape (no selected_date):
   * {
   *   selectedDate: "YYYY-MM-DD",   // first date with an open slot
   *   excluded: false,
   *   timeSlots: [{ startTime, endTime, available }],
   *   availableDates: [{ date: "YYYY-MM-DD" }]   // always BD4–BD8, no businessDay field
   * }
   *
   * On error (404):
   * {
   *   detail: {
   *     code: 2000,
   *     reason_code: "00",
   *     message: "No available appointment slots found between ...",
   *     availableDates: [{ date: "YYYY-MM-DD" }]
   *   }
   * }
   */
  const fetchInitialAvailability = async () => {
    try {
      const response = await getCalendarAvailability();
      const data = response.data || response;

      // availableDates always comes back as [{ date: "YYYY-MM-DD" }] — no businessDay
      if (!data.availableDates || data.availableDates.length === 0) {
        throw new Error(intl.formatMessage({ id: 'uiDashboard.resea.errors.2003' }));
      }

      const availableDates = data.availableDates.map((d) => {
        const [year, month, day] = d.date.split('-').map(Number);
        return {
          date: new Date(Date.UTC(year, month - 1, day)),
          dateStr: d.date,
        };
      });

      let firstDate;
      let timeSlots;

      // Backend returns selectedDate + timeSlots directly (no firstAvailableDay wrapper)
      if (data.selectedDate && data.timeSlots) {
        const [year, month, day] = data.selectedDate.split('-').map(Number);
        firstDate = new Date(Date.UTC(year, month - 1, day));
        timeSlots = data.timeSlots.map((slot, index) => ({
          id: index + 1,
          startTime: slot.startTime,
          endTime: slot.endTime,
          available: slot.available === true,
        }));
      } else {
        // Fallback: use first available date with no slots
        const [year, month, day] = data.availableDates[0].date.split('-').map(Number);
        firstDate = new Date(Date.UTC(year, month - 1, day));
        timeSlots = [];
      }

      return { availableDates, firstDate, firstDateSlots: timeSlots };
    } catch (error) {
      console.error('Error fetching initial availability:', error);
      throw error;
    }
  };

  /**
   * Fetches time slots for a specific date (selected_date query param).
   *
   * Backend response shape:
   * {
   *   selectedDate: "YYYY-MM-DD",
   *   excluded: false,
   *   timeSlots: [{ startTime, endTime, available }],
   *   availableDates: [{ date: "YYYY-MM-DD" }]
   * }
   *
   * On error (400/404):
   * {
   *   detail: {
   *     code: <number>,
   *     reason_code: "<string>",   // present for 2000 errors
   *     message: "<string>",
   *     availableDates: [...]      // present on 2000 errors
   *   }
   * }
   */
  const fetchTimeSlotsForDate = async (selectedDate) => {
    try {
      const selectedDateStr = selectedDate.toISOString().split('T')[0];
      const response = await getCalendarAvailability(selectedDateStr);
      const data = response.data || response;

      if (data.excluded) {
        return {
          slots: [],
          excluded: true,
          reason: data.reason || intl.formatMessage({ id: 'uiDashboard.resea.errors.dateNotAvailable' }),
        };
      }

      const slots = (data.timeSlots || []).map((slot, index) => ({
        id: index + 1,
        startTime: slot.startTime,
        endTime: slot.endTime,
        available: slot.available === true,
      }));

      return { slots, excluded: false };
    } catch (error) {
      console.error('Error fetching time slots:', error);
      throw error;
    }
  };

  const loadInitialAvailability = async () => {
    try {
      setLoading(true);
      const { availableDates, firstDate, firstDateSlots } = await fetchInitialAvailability();

      const hasValidSlots =
        firstDateSlots && firstDateSlots.length > 0 && firstDateSlots.some((slot) => slot.available);

      // No available dates or no open slots — skip scheduling automatically
      if (!availableDates || availableDates.length === 0 || !hasValidSlots) {
        if (onAppointmentSelected) {
          onAppointmentSelected({ noSlotsAvailable: true });
        }
        if (onComplete) {
          onComplete();
        }
        return;
      }

      setAvailableDates(availableDates);
      setSelectedDate(firstDate.toISOString().split('T')[0]);
      setTimeSlots(firstDateSlots);
      setLoading(false);
    } catch (err) {
      console.error('Error loading initial availability:', err);
      const errorMessage = getErrorMessage(err);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      setLoading(false);

      // On any error, treat as no slots and close
      if (onAppointmentSelected) {
        onAppointmentSelected({ noSlotsAvailable: true });
      }
      if (onComplete) {
        onComplete();
      }
    }
  };

  const handleDateChange = async (event) => {
    const newDateStr = event.target.value;
    setSelectedDate(newDateStr);
    setSelectedSlot(null);
    setLiveRegionMessage(`Date changed to ${formatDateForDisplay(newDateStr)}`);

    const dateObj = new Date(newDateStr + 'T00:00:00Z');

    try {
      setLoadingSlots(true);
      const { slots, excluded, reason } = await fetchTimeSlotsForDate(dateObj);

      if (excluded) {
        setSnackbar({ open: true, message: reason, severity: 'warning' });
        setTimeSlots([]);
      } else {
        const hasAvailableSlots = slots && slots.length > 0 && slots.some((s) => s.available);

        if (!hasAvailableSlots && isRescheduling) {
          if (onAppointmentSelected) {
            onAppointmentSelected({ noSlotsAvailable: true });
          }
          setSnackbar({
            open: true,
            message: intl.formatMessage({ id: 'uiDashboard.resea.noSlotsForDate' }),
            severity: 'info',
          });
          if (onComplete) {
            setTimeout(() => onComplete(), 1500);
          }
          return;
        }

        setTimeSlots(slots);
        const availableCount = slots.filter((s) => s.available).length;
        setLiveRegionMessage(`${availableCount} time slots available`);
      }

      setLoadingSlots(false);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      setLoadingSlots(false);
      setTimeSlots([]);
    }
  };

  const handleSlotSelect = (slot) => {
    if (slot.available) {
      setSelectedSlot(slot);
      setLiveRegionMessage(`Time slot ${formatTime(slot.startTime)} selected`);
    }
  };

  const handleContinueFromRequirement = () => {
    if (!acknowledgedRequirement) return;
    setCurrentStep('selection');
    setLiveRegionMessage(intl.formatMessage({ id: 'uiDashboard.resea.proceedingToSelection' }));
  };

  const handleSaveAppointment = () => {
    if (!selectedDate || !selectedSlot) return;

    const dateObj = new Date(selectedDate + 'T00:00:00Z');
    const appointmentData = {
      start_time: `${dateObj.toISOString().split('T')[0]}T${selectedSlot.startTime}:00`,
      claimant_date: getTodayDateString(),
      selectedDate: dateObj.toISOString().split('T')[0],
      selectedSlot: selectedSlot,
      displayDate: formatDateForDisplay(selectedDate),
      displayTime: `${formatTime(selectedSlot.startTime)} - ${formatTime(selectedSlot.endTime)}`,
    };

    if (onAppointmentSelected) {
      onAppointmentSelected(appointmentData);
    }
    if (onComplete) {
      onComplete();
    }
  };

  const handleCancel = () => {
    if (onAppointmentSelected) {
      onAppointmentSelected({ cancelled: true });
    }
    setSnackbar({
      open: true,
      message: intl.formatMessage({ id: 'uiDashboard.resea.skipConfirmation' }),
      severity: 'info',
    });
    if (onComplete) {
      setTimeout(() => onComplete(), 1000);
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDateForDisplay = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return intl.formatDate(date, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  };

  const renderTimeSlots = () => {
    if (!selectedDate) {
      return (
        <Box sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">
            <FormattedMessage id="uiDashboard.resea.selectDatePrompt" />
          </Typography>
        </Box>
      );
    }

    if (loadingSlots) {
      return (
        <Box sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress aria-label="Loading time slots" />
        </Box>
      );
    }

    if (timeSlots.length === 0) {
      return (
        <Box sx={{ minHeight: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">
            <FormattedMessage id="uiDashboard.resea.timeSlots.noSlotsAvailable" />
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography id="time-selection-heading" component="h2" variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
          <FormattedMessage id="uiDashboard.resea.availableTimes" />
        </Typography>

        <Box
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 1.5 }}
          role="group"
          aria-labelledby="time-selection-heading"
        >
          {timeSlots.map((slot) => {
            const timeLabel = formatTime(slot.startTime);
            const isSlotSelected = selectedSlot?.id === slot.id;
            const isAvailable = slot.available === true;

            return (
              <Button
                key={slot.id}
                onClick={() => handleSlotSelect(slot)}
                disabled={!isAvailable}
                variant={isSlotSelected ? 'contained' : 'outlined'}
                aria-label={`${timeLabel}${isSlotSelected ? ', selected' : ''}${!isAvailable ? ', unavailable' : ''}`}
                aria-pressed={isSlotSelected}
                sx={{ py: 1.5, fontSize: '0.875rem' }}
              >
                {timeLabel} CT
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  };

  const isShowSkip =
    window.location.origin === 'https://jobseeker.ar.uat1.recengine.org' ||
    window.location.origin === 'http://localhost:3000';
  const handleSkip = () => {
    onAppointmentSelected({ noSlotsAvailable: true });
  };

  const renderRequirementStep = () => (
    <>
      <DialogTitle ref={requirementSectionRef} tabIndex={-1} sx={{ '&:focus': { outline: 'none' } }}>
        <FormattedMessage id="uiDashboard.resea.title" />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
          <FormattedMessage id="uiDashboard.resea.description" />
        </Typography>

        <Alert severity="warning" icon={<WarningAmberIcon sx={{ mt: 0.5 }} fontSize="large" />} sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            <FormattedMessage id="uiDashboard.resea.warning" />
          </Typography>
        </Alert>

        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledgedRequirement}
                onChange={(e) => {
                  setAcknowledgedRequirement(e.target.checked);
                  setLiveRegionMessage(
                    e.target.checked
                      ? intl.formatMessage({ id: 'uiDashboard.resea.acknowledgedLive' })
                      : intl.formatMessage({ id: 'uiDashboard.resea.notAcknowledgedLive' })
                  );
                }}
                inputProps={{ 'aria-label': intl.formatMessage({ id: 'uiDashboard.resea.acknowledge' }) }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                <FormattedMessage id="uiDashboard.resea.acknowledge" />
              </Typography>
            }
          />
        </FormGroup>
      </DialogContent>
      <DialogActions
        sx={{ display: 'flex', justifyContent: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 1, pb: 3 }}
      >
        <Button variant="contained" onClick={handleContinueFromRequirement} disabled={!acknowledgedRequirement}>
          <FormattedMessage id="uiDashboard.resea.continueToSchedule" />
        </Button>
        {isShowSkip && (
          <Button variant="outlined" color="warning" onClick={handleSkip}>
            <FormattedMessage id="uiDashboard.resea.continueWithoutAppointment" />
          </Button>
        )}
      </DialogActions>
    </>
  );

  const renderSelectionStep = () => (
    <>
      <DialogTitle>
        <FormattedMessage id="uiDashboard.resea.selectionTitle" />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
          <FormattedMessage id="uiDashboard.resea.selectionSubtitle" />
        </Typography>

        <Box sx={{ mb: 3 }} role="region" aria-labelledby="date-selection-heading">
          <Typography id="date-selection-heading" component="h2" variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            <FormattedMessage id="uiDashboard.resea.dateSelectorLabel" />
          </Typography>

          <FormControl variant="filled" sx={{ width: { xs: '100% !important', md: '408px !important' } }}>
            <InputLabel id="date-select-label" htmlFor="date-select-input">
              {intl.formatMessage({ id: 'uiDashboard.resea.dateSelectorLabel' })}
            </InputLabel>
            <Select
              id="date-select-input"
              labelId="date-select-label"
              name="date-select"
              value={selectedDate}
              onChange={handleDateChange}
              MenuProps={{ sx: { maxHeight: 440, maxWidth: 200 } }}
              sx={{ '.MuiFilledInput-input:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' } }}
              variant="filled"
              inputProps={{ 'aria-label': intl.formatMessage({ id: 'uiDashboard.resea.dateSelectorLabel' }) }}
            >
              {availableDates.map((dateObj) => (
                <MenuItem
                  key={dateObj.dateStr}
                  value={dateObj.dateStr}
                  sx={{ fontSize: 13, whiteSpace: 'break-spaces', lineHeight: '18px', color: grey['800'] }}
                >
                  {formatDateForDisplay(dateObj.dateStr)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box role="region" aria-labelledby="time-selection-heading">
          {renderTimeSlots()}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ display: 'flex', justifyContent: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 1, pb: 3 }}
      >
        {!isRescheduling && (
          <Button
            variant="outlined"
            onClick={() => {
              setCurrentStep('requirement');
              setLiveRegionMessage(intl.formatMessage({ id: 'uiDashboard.resea.returnToRequirement' }));
            }}
          >
            <FormattedMessage id="uiDashboard.resea.back" />
          </Button>
        )}
        <Button variant="contained" onClick={handleSaveAppointment} disabled={!selectedSlot}>
          <FormattedMessage id="uiDashboard.resea.next" />
        </Button>
      </DialogActions>
    </>
  );

  if (loading) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress size={60} aria-label="Loading RESEA appointment system" />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
        >
          {liveRegionMessage}
        </Box>

        {currentStep === 'requirement' && renderRequirementStep()}
        {currentStep === 'selection' && renderSelectionStep()}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

ReseaAppointmentScheduler.propTypes = {
  open: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  onAppointmentSelected: PropTypes.func,
  onSkip: PropTypes.func,
  isRescheduling: PropTypes.bool,
};

export { ReseaAppointmentScheduler as default };

/**
 * Exported helper — use this in ContinuedClaim.jsx when calling bookAppointment()
 * to determine if the error represents a scheduling conflict (slot taken by someone
 * else between selection and submission).
 *
 * Backend returns error code 2000 with reason_code "03" (mixed) or "04" (all booked)
 * for slot conflicts. reason_code "01"/"02" are OOF/calendar blocks, not conflicts.
 *
 * @param {Error} error — axios error from bookAppointment()
 * @returns {boolean}
 */
export const isSlotConflictError = (error) => {
  const detail = error.response?.data?.detail || error.response?.data || {};
  const errorCode = detail.code;
  const reasonCode = detail.reason_code;
  return errorCode === 2000 && (reasonCode === '03' || reasonCode === '04');
};
